import { Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { COMPETENCY_LEVEL_LABELS } from '../personnelTypes';
import type { PersonnelDetail, PersonnelSpecialSkill } from '../personnelTypes';

/**
 * Special skills and hobbies — CS Form 212, section VIII, item 31.
 *
 * **BEST first, and this is the one section of the 201 file that is not ordered by a date.** The
 * histories above it answer "what did they do and when"; this answers "what are they good at",
 * so a reader scanning it wants the Expert rows before the Basic ones. The server sends them in
 * that order and the panel opens on it, or the profile and its own table would disagree about
 * which row is first.
 *
 * **Searched, sorted and paged in memory**, which is correct here: the rule against client-side
 * filtering is about a PAGED list whose filter applies only to the rows on screen. These arrive
 * whole with the record, so the filter sees everything there is.
 */
export function SpecialSkillsPanel({
  record,
  onAdd,
  onEdit,
  onDelete,
  onBulkDelete,
  onRestore,
}: {
  record: PersonnelDetail;
  onAdd: () => void;
  onEdit: (row: PersonnelSpecialSkill) => void;
  onDelete: (row: PersonnelSpecialSkill) => void;
  onBulkDelete: (rows: PersonnelSpecialSkill[]) => void;
  onRestore: (row: PersonnelSpecialSkill) => void;
}) {

  const [showDeleted, setShowDeleted] = useState(false);

  const rows = useMemo(
    () => (showDeleted ? record.specialSkills : record.specialSkills.filter((x) => x.isActive)),
    [record.specialSkills, showDeleted],
  );

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? rows.filter((x) =>
            [x.name, x.categoryName, x.remarks]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : rows;

      const field = query.sort?.replace(/^-/, '') ?? 'proficiencyLevel';
      const descending = query.sort?.startsWith('-') ?? false;
      const direction = descending ? -1 : 1;

      const sorted = [...matched].sort((a, b) => {
        if (field === 'proficiencyLevel') {
          /*
            A NUMBER on an ORDERED ladder, so it compares as one — sorting the LABELS would read
            Advanced, Basic, Expert, Intermediate, which is alphabetical order pretending to be
            a ranking. Ties break on the name, matching the server's own ordering.
          */
          const gap = (a.proficiencyLevel - b.proficiencyLevel) * direction;
          return gap !== 0 ? gap : a.name.localeCompare(b.name);
        }

        if (field === 'yearsOfExperience') {
          /*
            A NUMBER, so it compares as one — string order would put 9 above 15. Rows with no
            recorded years sort last whichever way the column is pointed: null here means nobody
            wrote it down, and burying those among the totals would read as zero years, which is
            a different answer the column can also hold.
          */
          const av = a.yearsOfExperience ?? null;
          const bv = b.yearsOfExperience ?? null;
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return (av - bv) * direction;
        }

        const pick = (row: PersonnelSpecialSkill) =>
          field === 'categoryName'
            ? row.categoryName
            : field === 'remarks'
              ? (row.remarks ?? '')
              : row.name;

        return String(pick(a)).localeCompare(String(pick(b))) * direction;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [rows],
  );

  const state = useDataTable<PersonnelSpecialSkill>({
    fetchPage,
    pageSize: 10,

    // Best first, matching the order the server sends them in. See the panel's own doc comment
    // for why this section does not sort by a date like the histories around it.
    defaultSort: '-proficiencyLevel',
  });

  const columns: DataColumn<PersonnelSpecialSkill>[] = [
    {
      /*
        The ANCHOR is the skill, which is what identifies the row. Not the category, which titles
        four rows "Technical Skill" and tells a reader nothing about which entry they are looking
        at — the mistake the family and education panels each made first.
      */
      key: 'name',
      header: 'Skill / hobby',
      width: 240,
      sortable: true,
      render: (x) => <TextCell value={x.name} />,
    },
    {
      key: 'categoryName',
      header: 'Category',
      width: 180,
      sortable: true,
      summary: true,
      /*
        Plain text, not mono: a category is a word somebody chose from a vocabulary, not a
        machine value. Resolved through the join on every read, so correcting the catalog
        corrects this.
      */
      render: (x) => <TextCell value={x.categoryName} />,
    },
    {
      key: 'proficiencyLevel',
      header: 'Proficiency',
      width: 140,
      sortable: true,
      summary: true,
      /*
        The LABEL, sorted on the NUMBER — see `fetchPage`. Never `0`: the server refuses Not
        rated, so the map can only ever be read at 1 to 4 here.
      */
      render: (x) => <TextCell value={COMPETENCY_LEVEL_LABELS[x.proficiencyLevel]} />,
    },
    {
      key: 'yearsOfExperience',
      header: 'Years',
      width: 100,
      align: 'right',
      sortable: true,
      /*
        `numeric` as well as `mono`: years are a quantity, so a zero dims like every other zero in
        the product — and a zero here is a real answer (took it up this year) rather than an
        absence, which is exactly the distinction the muted treatment draws. An unrecorded total
        is null and renders as an em dash.
      */
      render: (x) => (
        <TextCell
          value={
            x.yearsOfExperience === null || x.yearsOfExperience === undefined
              ? null
              : String(x.yearsOfExperience)
          }
          mono
          numeric
        />
      ),
    },
    {
      key: 'remarks',
      header: 'Remarks',
      width: 260,
      sortable: true,
      render: (x) => <TextCell value={x.remarks ?? null} />,
    },
    {
      /*
        A panel whose rows can be deleted owes a status column: without one a deleted row carries
        no marker while Show deleted is on, which is the one state a row must never be silent
        about. `<DataTablePanel>` moves it to the end of the data columns itself.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        x.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <DataTablePanel<PersonnelSpecialSkill>
        id="personnel-special-skill"
        state={state}
        /*
          The first column opens the row as a key/value preview. A table row compares records; it
          is a poor way to read ONE, because the columns are spread across a scroll and the narrow
          ones truncate. The preview is built from these same `columns`, so it cannot drift from
          the table or miss one somebody adds.
        */
        // The size of the modal that EDITS this record, so reading a row and correcting it
        // happen at one width rather than the dialog changing shape between them.
        rowPreview={{ title: 'Special skill / hobby', size: '2xl' }}
        columns={columns}
        rowKey={(x) => x.id}
        searchPlaceholder="Search skill, category or remarks"
        onCreate={onAdd}
        createPermission="personnel.update"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before editing the record.'
        }
        /*
          The kebab's entries. The shell owns the gutter, the menu and the right-click shortcut —
          this is only what goes IN the menu, and the same array feeds the table and the list, so
          the two cannot offer different actions on one row.

          Delete and Restore are the two halves of one state, so only the applicable one is
          offered. That is not the disable-for-state case: "restore a live row" is not something
          the reader is part-way through, it is simply not an action here.
        */
        rowActions={(x) => [
          {
            key: `edit-${x.id}`,
            label: 'Edit',
            permission: 'personnel.update',
            disabledReason: x.isActive
              ? null
              : 'This record is deleted. Restore it before editing.',
            onClick: () => onEdit(x),
          },
          x.isActive
            ? {
                key: `delete-${x.id}`,
                label: 'Delete',
                permission: 'personnel.update',
                destructive: true,
                onClick: () => onDelete(x),
              }
            : {
                key: `restore-${x.id}`,
                label: 'Restore',
                permission: 'personnel.update',
                onClick: () => onRestore(x),
              },
        ]}
        otherInfo={(x) => ({
          createdAt: x.createdAt,
          createdByName: x.createdByName,
          updatedAt: x.updatedAt,
          updatedByName: x.updatedByName,
        })}
        /*
          The checkbox gutter is ON, which is the default — `selectable={false}` is only for a
          table nothing can ever be done to, never for one whose bulk action is merely unbuilt.
          So the bulk action ships with it.
        */
        bulkActions={(selected) => {
          const deletable = selected.filter((x) => x.isActive);

          return [
            {
              key: 'delete-special-skills',
              label: `Delete ${deletable.length} skill${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? 'This employee is deleted. Restore them before editing the record.'
                : deletable.length === 0
                  ? 'Every selected skill is already deleted.'
                  : null,
              onClick: () => onBulkDelete(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No special skills or hobbies recorded yet."
        emptySearchMessage="No skill matches that search."
      />
    </ProfileSection>
  );
}
